import { useMutation, UseMutationOptions, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { buildErrorMessage } from 'utils'

import { AlmanacFileSummary, ArticleFlat, HandbookItemType, CrossPostGroup } from 'types'
import { almanacFileKeys } from './almanacFileKeys'
import * as Actions from 'redux/actions'
import { articlesKeys } from '../articles/articlesKeys'
import produce from 'immer'
import { useDispatch } from 'react-redux'
import { useRootStore } from 'stores/RootStore'
import { values } from 'mobx'
import { useHandbook } from 'components/Handbook2_0/hooks'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { last } from 'lodash'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

interface Props {
  key: string
}

export interface UpdatePayload {
  version_control_mode: boolean
  title: string
  emoji: string | null
  channel_emoji: ChannelType | null
  cross_post_group_id?: number | null
  view_metadata?: {
    view: 'calendar_view' | 'list_view'
  }
}

interface CrossPostGroupData {
  cross_post_group: CrossPostGroup
}

interface MutationContext {
  previousCrossPostGroups?: Array<{ queryKey: any; data: CrossPostGroupData }>
  previousAlmanacFile?: any
  previousFolder?: any
  previousInfiniteQueries?: Array<{ queryKey: any; data: any }>
}

export type Options = Omit<
  UseMutationOptions<{ almanac_file: AlmanacFileSummary }, Error, Partial<UpdatePayload>, MutationContext>,
  'mutationKey' | 'mutationFn'
>

export const useUpdateAlmanacFile = ({ key }: Props, options?: Options) => {
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  const rootStore = useRootStore()
  const handbook = useHandbook()
  const workspaceId = useCurrentWorkspaceId()

  return useMutation<{ almanac_file: AlmanacFileSummary }, Error, Partial<UpdatePayload>, MutationContext>(
    almanacFileKeys.update(key),
    async (attributes) => {
      const response = await sendToApi<{ almanac_file: AlmanacFileSummary }>(`/almanac_files/${key}`, {
        method: 'PUT',
        body: attributes,
        params: { include: ['version_control_mode.metadata'] },
      })

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error(
          buildErrorMessage(response.data.errors) ||
            'An error occurred! Our team has been notified and we are looking into it.',
        )
      }

      const updatedAlmanacFile = response.data.almanac_file
      const parentKey = last(updatedAlmanacFile.ancestors)?.seo_string.split('-').pop()
      const handbookTreeStore = rootStore.handbooks.handbooks.get(parentKey || handbook?.key || '')

      rootStore.files.add(updatedAlmanacFile)
      handbookTreeStore?.refresh()

      dispatch(Actions.articleMetaUpsert({ id: key, almanac_file: updatedAlmanacFile }))
      queryClient.setQueriesData<{ article: ArticleFlat } | undefined>([articlesKeys.prefix, 'detail', key], (old) => {
        return produce(old, (draft) => {
          if (!draft) {
            return
          }

          draft.article = { ...draft.article, almanac_file: updatedAlmanacFile }
        })
      })

      const handbookTreeItem = values(rootStore.handbooks.items).find(
        (item) =>
          item.type === HandbookItemType.NestedHandbook && item.data.folder?.almanac_file.id === updatedAlmanacFile.id,
      )

      // Root level folder case: We want to invalidate the whole left rail.
      //
      // TODO: Refactor the usage of FileTreeStore so that we can target which
      // file to updated more precisely.
      if (!handbookTreeItem && handbook) {
        ;[
          ['workspace', handbook.workspace_id, 'pinned_files'],
          ['workspace', handbook.workspace_id, 'files', 'shared', { items: 50 }],
          ['workspace', handbook.workspace_id, 'files', 'personal', { items: 50 }],
        ].forEach((key) => queryClient.invalidateQueries(key))
      }

      if (handbookTreeItem?.data.folder) {
        handbookTreeItem.updateAttrs({
          folder: {
            ...handbookTreeItem.data.folder,
            almanac_file: {
              ...handbookTreeItem.data.folder?.almanac_file,
              version_control_mode: updatedAlmanacFile.version_control_mode,
            },
          },
        })
      }

      return response.data
    },
    {
      onMutate: async (attributes): Promise<MutationContext> => {
        // If cross_post_group_id is null, we need to optimistically remove the file from the cross post group
        if (attributes.cross_post_group_id === null) {
          // Cancel any outgoing refetches for cross post groups
          await queryClient.cancelQueries({ queryKey: ['workspaces', workspaceId, 'cross_post_groups'] })

          // Find all cross post group queries that might contain this file
          const crossPostGroupQueries = queryClient.getQueriesData({
            queryKey: ['workspaces', workspaceId, 'cross_post_groups'],
          })

          const previousCrossPostGroups: Array<{ queryKey: any; data: CrossPostGroupData }> = []

          crossPostGroupQueries.forEach(([queryKey, data]) => {
            const typedData = data as CrossPostGroupData | undefined
            if (typedData?.cross_post_group?.almanac_files) {
              // Snapshot the previous value
              previousCrossPostGroups.push({ queryKey, data: typedData })

              // Optimistically remove the file from the cross post group
              queryClient.setQueryData(queryKey, ((old: CrossPostGroupData | undefined) => {
                if (!old?.cross_post_group?.almanac_files) return old

                return {
                  ...old,
                  cross_post_group: {
                    ...old.cross_post_group,
                    almanac_files: old.cross_post_group.almanac_files.filter((file) => file.key !== key),
                  },
                }
              }) as any)
            }
          })

          return { previousCrossPostGroups }
        }

        return {}
      },
      onError: (err, attributes, context) => {
        // Rollback optimistic updates if cross_post_group_id was null
        if (attributes.cross_post_group_id === null && context?.previousCrossPostGroups) {
          context.previousCrossPostGroups.forEach(({ queryKey, data }) => {
            queryClient.setQueryData(queryKey, data)
          })
        }
      },
      onSettled: (data, error, attributes) => {
        // If cross_post_group_id was null, invalidate cross post group queries
        if (attributes.cross_post_group_id === null) {
          queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'cross_post_groups'] })
        }
      },
      ...options,
    },
  )
}
